export type Language = 'zh' | 'en';

export interface Translations {
  // Common
  brand: string;
  slogan: string;
  tagline: string;
  domain: string;
  
  // Auth
  login: string;
  register: string;
  logout: string;
  email: string;
  password: string;
  confirmPassword: string;
  loginButton: string;
  registerButton: string;
  loggingIn: string;
  registering: string;
  
  // Login page
  loginTitle: string;
  loginSubtitle: string;
  loginPromo: string;
  noAccount: string;
  registerNow: string;
  
  // Register page
  registerTitle: string;
  registerPromo: string;
  sendCode: string;
  sendingCode: string;
  verificationCode: string;
  codeExpiry: string;
  resendCode: string;
  resendCooldown: string;
  backToEmail: string;
  hasAccount: string;
  loginNow: string;
  codeSentTo: string;
  setPassword: string;
  
  // Dashboard
  dashboard: string;
  welcome: string;
  accountInfo: string;
  subscriptionStatus: string;
  trafficUsage: string;
  expiresAt: string;
  expired: string;
  notSubscribed: string;
  used: string;
  remaining: string;
  unlimited: string;
  
  // Subscription
  subscribe: string;
  subscriptionLink: string;
  subscriptionNote: string;
  
  // Errors
  loginFailed: string;
  registerFailed: string;
  invalidEmail: string;
  passwordMismatch: string;
  passwordTooShort: string;
  codeExpired: string;
  sendCodeFailed: string;
  
  // Legal
  userAgreement: string;
  privacyPolicy: string;
  termsOfService: string;
  
  // Dashboard specific
  loading: string;
  accountEmail: string;
  plan: string;
  expiryDate: string;
  status: string;
  statusActive: string;
  statusInactive: string;
  trafficUsed: string;
  connectionConfig: string;
  copyLink: string;
  linkCopied: string;
  viewQRCode: string;
  qrCodeHint: string;
  purchaseMembership: string;
  membershipBenefit: string;
  purchaseButton: string;
  purchaseHint: string;
  purchaseContact: string;
  redeemCode: string;
  redeemCodePlaceholder: string;
  redeemButton: string;
  redeeming: string;
  redeemSuccess: string;
  redeemError: string;
  contactUs: string;
  teamName: string;
  teamLocation: string;
  teamNote: string;
  usageTipMobile: string;
  usageTipDesktop: string;
  refreshPage: string;
  
  // Referral
  referralEmailLabel: string;
  referralEmailPlaceholder: string;
  referralSectionTitle: string;
  referralHint: string;
  referralCopyEmail: string;
  referralEmailCopied: string;
  referralConfirm: string;
  referralShareMessage: string;

  // Tutorial
  tutorialSectionTitle: string;
  iosWarning: string;
  showGuide: string;
  hideGuide: string;
  downloadHiddify: string;
  openApp: string;
  importConfig: string;
  startConnect: string;
  installApp: string;
  addConfig: string;
  downloadAndInstall: string;
  macosWarning: string;
  githubMirrorHint: string;
}

export const translations: Record<Language, Translations> = {
  zh: {
    // Common
    brand: '都行嘛网络加速器',
    slogan: '流量买断制，永不过期的加速器',
    tagline: '程序员、科研工作者的必备工具',
    domain: '永久网址（都行嘛拼音）：douxingma.com',
    
    // Auth
    login: '登录',
    register: '注册',
    logout: '退出登录',
    email: '邮箱地址',
    password: '密码',
    confirmPassword: '确认密码',
    loginButton: '立即登录',
    registerButton: '立即注册',
    loggingIn: '登录中...',
    registering: '注册中...',
    
    // Login page
    loginTitle: '登录账户',
    loginSubtitle: '欢迎回来，继续畅游网络世界',
    loginPromo: '🎁 注册即送 5GB 永久流量｜每推荐一位好友再得 10GB',
    noAccount: '还没有账号？',
    registerNow: '立即注册',
    
    // Register page
    registerTitle: '注册账户',
    registerPromo: '🎁 注册即送：5GB 永久流量，不限时、不过期',
    sendCode: '发送验证码',
    sendingCode: '发送中...',
    verificationCode: '6位验证码',
    codeExpiry: '验证码有效期：',
    resendCode: '重新发送验证码',
    resendCooldown: '秒后可重新发送',
    backToEmail: '返回修改邮箱',
    hasAccount: '已有账号？',
    loginNow: '立即登录',
    codeSentTo: '验证码已发送至',
    setPassword: '设置密码（至少6位）',
    
    // Dashboard
    dashboard: '控制面板',
    welcome: '欢迎',
    accountInfo: '账户信息',
    subscriptionStatus: '订阅状态',
    trafficUsage: '流量使用',
    expiresAt: '流量有效期',
    expired: '流量已用完',
    notSubscribed: '永久有效',
    used: '已用',
    remaining: '剩余',
    unlimited: '无限制',
    
    // Subscription
    subscribe: '订阅套餐',
    subscriptionLink: '点击订阅',
    subscriptionNote: '流量买断制，一次购买永久有效，没有月租',
    
    // Errors
    loginFailed: '登录失败',
    registerFailed: '注册失败，请检查验证码是否正确',
    invalidEmail: '请输入有效的邮箱地址',
    passwordMismatch: '两次输入的密码不一致',
    passwordTooShort: '密码长度至少为 6 位',
    codeExpired: '验证码已过期，请重新发送',
    sendCodeFailed: '验证码发送失败，请重试',
    
    // Legal
    userAgreement: '用户协议',
    privacyPolicy: '隐私政策',
    termsOfService: '服务条款',
    
    // Dashboard specific
    loading: '加载中...',
    accountEmail: '邮箱',
    plan: '套餐',
    expiryDate: '流量有效期',
    status: '状态',
    statusActive: '✅ 正常',
    statusInactive: '❌ 异常',
    trafficUsed: '已用',
    connectionConfig: '连接配置',
    copyLink: '📋 复制连接链接',
    linkCopied: '✓ 已复制，请打开 Hiddify 粘贴',
    viewQRCode: '📷 查看二维码（电脑扫码用）',
    qrCodeHint: '在电脑上打开此页面，用手机 Hiddify 扫码导入',
    purchaseMembership: '💎 流量快用完了？',
    membershipBenefit: '¥29 买断 100GB 流量，永不过期，用完再买',
    purchaseButton: '💳 立即购买 100GB 永久流量 · ¥29（微信 / 支付宝）',
    purchaseHint: '✅ 购买后请刷新页面，查看账户信息更新',
    purchaseContact: '❓ 没有反应？请把购买记录截屏和你的账户邮箱发给',
    redeemCode: '已有升级码？在此兑换：',
    redeemCodePlaceholder: '输入升级码',
    redeemButton: '立即兑换',
    redeeming: '兑换中...',
    redeemSuccess: '兑换成功！已为您添加 100GB 永久流量',
    redeemError: '兑换失败，请检查升级码是否正确',
    contactUs: '📧 联系我们',
    teamName: '都行嘛（douxingma.com）是一支由美国西海岸工程师组成的网络服务团队，致力于为用户提供稳定、安全的网络访问服务。',
    teamLocation: '如有任何问题，欢迎发送邮件至 douxingma@zohomail.com',
    teamNote: '我们通常在 24–48 小时内回复。',
    usageTipMobile: 'Hiddify：复制下方链接，打开 Hiddify 应用，点击"新建"或"+"，粘贴链接即可导入\n\nv2box：复制下方链接，打开 v2box → 底部选择"配置" → 右上角点击 + 号 → 选择"从剪贴板导入"',
    usageTipDesktop: '复制下方链接，在电脑的 Hiddify 中粘贴导入。如需给手机配置，可在电脑上打开此页面，用手机扫描二维码',
    refreshPage: '刷新页面',
    
    // Referral
    referralEmailLabel: '推广人邮箱（选填）',
    referralEmailPlaceholder: '填写邀请你的人的邮箱',
    referralSectionTitle: '🎁 推广赚流量',
    referralHint: '把你的邮箱分享给朋友，让他们注册时填入。每成功推荐一人，你将获得 10GB 永久流量奖励，不设上限。',
    referralCopyEmail: '📋 复制邀请文案',
    referralEmailCopied: '✓ 已复制',
    referralConfirm: '成功注册将为邀请者加 10GB 永久流量',
    referralShareMessage: '邀请你一起使用都行嘛 VPN：https://douxingma.com\n注册时请在“推广人邮箱”填写：{email}\n注册即送 5GB 永久流量，不限时、不过期。',

    // Tutorial
    tutorialSectionTitle: '📖 使用教程',
    iosWarning: '⚠️ 中国区 App Store 没有 v2box，请参考下列教程下载',
    showGuide: '📖 查看详细下载指南',
    hideGuide: '收起下载指南',
    downloadHiddify: '下载 Hiddify',
    openApp: '打开应用',
    importConfig: '导入配置',
    startConnect: '开始连接',
    installApp: '安装应用',
    addConfig: '添加配置',
    downloadAndInstall: '下载并安装',
    macosWarning: '⚠️ 修复损坏提示',
    githubMirrorHint: '',
  },
  
  en: {
    // Common
    brand: 'Douxingma VPN',
    slogan: 'Buy Data Once. It Never Expires.',
    tagline: 'Essential for Developers & Researchers',
    domain: 'Permanent URL: douxingma.com',
    
    // Auth
    login: 'Login',
    register: 'Sign Up',
    logout: 'Log Out',
    email: 'Email Address',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    loginButton: 'Log In',
    registerButton: 'Create Account',
    loggingIn: 'Logging in...',
    registering: 'Creating account...',
    
    // Login page
    loginTitle: 'Welcome Back',
    loginSubtitle: 'Log in to continue your journey',
    loginPromo: '🎁 Sign up: 5GB free, never expires｜Refer a friend: earn 10GB',
    noAccount: "Don't have an account?",
    registerNow: 'Sign Up',
    
    // Register page
    registerTitle: 'Create Your Account',
    registerPromo: '🎁 Sign up bonus: 5GB free data that never expires',
    sendCode: 'Send Code',
    sendingCode: 'Sending...',
    verificationCode: '6-digit code',
    codeExpiry: 'Expires in: ',
    resendCode: 'Resend Code',
    resendCooldown: 's',
    backToEmail: 'Change Email',
    hasAccount: 'Already have an account?',
    loginNow: 'Log In',
    codeSentTo: 'Code sent to',
    setPassword: 'Password (min 6 characters)',
    
    // Dashboard
    dashboard: 'Dashboard',
    welcome: 'Welcome',
    accountInfo: 'Account Info',
    subscriptionStatus: 'Subscription',
    trafficUsage: 'Data Usage',
    expiresAt: 'Data Validity',
    expired: 'Out of data',
    notSubscribed: 'Never expires',
    used: 'Used',
    remaining: 'Left',
    unlimited: 'Unlimited',
    
    // Subscription
    subscribe: 'Subscribe',
    subscriptionLink: 'Get Started',
    subscriptionNote: 'Pay once, use it whenever — your data never expires',
    
    // Errors
    loginFailed: 'Login failed',
    registerFailed: 'Registration failed. Please check your code.',
    invalidEmail: 'Please enter a valid email',
    passwordMismatch: 'Passwords don\'t match',
    passwordTooShort: 'Password must be at least 6 characters',
    codeExpired: 'Code expired. Please request a new one.',
    sendCodeFailed: 'Failed to send code. Please try again.',
    
    // Legal
    userAgreement: 'User Agreement',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    
    // Dashboard specific
    loading: 'Loading...',
    accountEmail: 'Email',
    plan: 'Plan',
    expiryDate: 'Data Validity',
    status: 'Status',
    statusActive: '✅ Active',
    statusInactive: '❌ Inactive',
    trafficUsed: 'Used',
    connectionConfig: 'Connection',
    copyLink: '📋 Copy Link',
    linkCopied: '✓ Copied! Paste in Hiddify',
    viewQRCode: '📷 QR Code (Desktop)',
    qrCodeHint: 'Open on desktop, scan with mobile Hiddify',
    purchaseMembership: '💎 Running low on data?',
    membershipBenefit: '¥29 for 100GB that never expires — top up when you run out',
    purchaseButton: '💳 Buy 100GB · ¥29 (WeChat / Alipay)',
    purchaseHint: '✅ Refresh page after purchase',
    purchaseContact: '❓ Issues? Email screenshot to',
    redeemCode: 'Have a code? Redeem here:',
    redeemCodePlaceholder: 'Enter code',
    redeemButton: 'Redeem',
    redeeming: 'Redeeming...',
    redeemSuccess: 'Success! Added 100GB that never expires',
    redeemError: 'Invalid code. Please check and try again.',
    contactUs: '📧 Contact Us',
    teamName: 'Douxingma (douxingma.com) is a network service team composed of engineers from the US West Coast, dedicated to providing stable and secure network access services.',
    teamLocation: 'For any questions, please email us at douxingma@zohomail.com',
    teamNote: 'We typically respond within 24–48 hours.',
    usageTipMobile: 'Hiddify: Copy link → Open app → Tap "New" or "+" → Paste\n\nv2box: Copy link → Open app → "Config" → Tap + → "Import from clipboard"',
    usageTipDesktop: 'Copy link and paste in Hiddify. For mobile setup, open this page on desktop and scan QR code.',
    refreshPage: 'Refresh',
    
    // Referral
    referralEmailLabel: 'Referrer Email (optional)',
    referralEmailPlaceholder: 'Email of the person who invited you',
    referralSectionTitle: '🎁 Refer & Earn Data',
    referralHint: 'Share your email with friends. When they register with your email, you earn 10GB that never expires. No cap.',
    referralCopyEmail: '📋 Copy Invite Text',
    referralEmailCopied: '✓ Copied',
    referralConfirm: 'Your referrer earns 10GB when you sign up',
    referralShareMessage: 'Join me on Douxingma VPN: https://douxingma.com\nWhen signing up, enter this referrer email: {email}\nNew users get 5GB free data that never expires.',

    // Tutorial
    tutorialSectionTitle: '📖 Setup Guide',
    iosWarning: '⚠️ v2box is not available in China App Store. See the guide below to download.',
    showGuide: '📖 Show Download Guide',
    hideGuide: 'Hide Guide',
    downloadHiddify: 'Download Hiddify',
    openApp: 'Open App',
    importConfig: 'Import Config',
    startConnect: 'Connect',
    installApp: 'Install',
    addConfig: 'Add Config',
    downloadAndInstall: 'Download & Install',
    macosWarning: '⚠️ Fix "App Damaged" Error',
    githubMirrorHint: '💡 Mirror recommended for faster download in China',
  },
};

export const getTranslation = (lang: Language): Translations => {
  return translations[lang];
};
